<template>
    <div class="change-owner-config">
        <!-- Method Selection -->
        <div class="config-field">
            <label>{{ $t("contactModuleRules.changeOwnerConfig.method") }}</label>
            <a-radio-group v-model="localConfig.method" @change="onMethodChange">
                <a-radio-button value="static">{{ $t("contactModuleRules.changeOwnerConfig.static") }}</a-radio-button>
                <a-radio-button value="algorithmic">{{ $t("contactModuleRules.changeOwnerConfig.algorithmic") }}</a-radio-button>
                <a-radio-button value="random">{{ $t("contactModuleRules.changeOwnerConfig.random") }}</a-radio-button>
            </a-radio-group>
        </div>

        <!-- Static Method -->
        <div v-if="localConfig.method === 'static'" class="static-config">
            <div class="config-field">
                <label>{{ $t("contactModuleRules.changeOwnerConfig.userSelect") }}</label>
                <a-checkbox v-model="isOwnerless" @change="onOwnerlessChange">
                    {{ $t("contactModuleRules.changeOwnerConfig.assignOwnerless") }}
                </a-checkbox>
            </div>

            <div v-if="!isOwnerless" class="config-field">
                <a-select
                    v-model="localConfig.staticUserId"
                    show-search
                    :placeholder="$t('contactModuleRules.changeOwnerConfig.selectUser')"
                    style="width: 100%;"
                    :filter-option="filterOption"
                    @change="emitChange"
                >
                    <a-select-option
                        v-for="user in users"
                        :key="user._id"
                        :value="user._id"
                    >
                        {{ user.name }}
                    </a-select-option>
                </a-select>
            </div>
        </div>

        <!-- Algorithmic Method -->
        <div v-if="localConfig.method === 'algorithmic'" class="algorithmic-config">
            <!-- User Selection -->
            <div class="config-field">
                <label>{{ $t("contactModuleRules.changeOwnerConfig.userList") }}</label>
                <a-checkbox v-model="localConfig.useVisibilityControl" @change="onVisibilityControlChange">
                    {{ $t("contactModuleRules.changeOwnerConfig.channelAccessUsers") }}
                </a-checkbox>
            </div>

            <div v-if="!localConfig.useVisibilityControl" class="config-field">
                <a-select
                    v-model="localConfig.userList"
                    mode="multiple"
                    :placeholder="$t('contactModuleRules.changeOwnerConfig.selectUsers')"
                    style="width: 100%;"
                    :filter-option="filterOption"
                    @change="emitChange"
                >
                    <a-select-option
                        v-for="user in users"
                        :key="user._id"
                        :value="user._id"
                    >
                        {{ user.name }}
                    </a-select-option>
                </a-select>
            </div>

            <!-- Algorithm Settings -->
            <div class="config-field">
                <label>{{ $t("contactModuleRules.changeOwnerConfig.algorithm") }}</label>
                <a-select 
                    v-model="localConfig.algorithmSettings.algorithm" 
                    style="width: 100%;"
                    @change="emitChange"
                >
                    <a-select-option value="least_contact">{{ $t("contactModuleRules.changeOwnerConfig.leastContact") }}</a-select-option>
                    <a-select-option value="round_robin">{{ $t("contactModuleRules.changeOwnerConfig.roundRobin") }}</a-select-option>
                    <a-select-option value="ordered">{{ $t("contactModuleRules.changeOwnerConfig.ordered") }}</a-select-option>
                </a-select>
                <small v-if="localConfig.algorithmSettings.algorithm === 'ordered'">
                    {{ $t("contactModuleRules.changeOwnerConfig.orderedHint") }}
                </small>
            </div>

            <!-- Date Range (ordered algoritmasında zaman penceresi yok) -->
            <div v-if="localConfig.algorithmSettings.algorithm !== 'ordered'" class="config-field">
                <label>{{ $t("contactModuleRules.changeOwnerConfig.dateRange") }}</label>
                <a-select 
                    v-model="localConfig.algorithmSettings.dateRange" 
                    style="width: 100%;"
                    @change="onDateRangeChange"
                >
                    <a-select-option value="today">{{ $t("contactModuleRules.changeOwnerConfig.today") }}</a-select-option>
                    <a-select-option value="last_7_days">{{ $t("contactModuleRules.changeOwnerConfig.last7Days") }}</a-select-option>
                    <a-select-option value="last_30_days">{{ $t("contactModuleRules.changeOwnerConfig.last30Days") }}</a-select-option>
                    <a-select-option value="last_n_min">{{ $t("contactModuleRules.changeOwnerConfig.lastNMin") }}</a-select-option>
                    <a-select-option value="last_n_hours">{{ $t("contactModuleRules.changeOwnerConfig.lastNHours") }}</a-select-option>
                    <a-select-option value="last_n_days">{{ $t("contactModuleRules.changeOwnerConfig.lastNDays") }}</a-select-option>
                </a-select>
            </div>

            <!-- Custom değer girişi (last_n_* pencereleri için) -->
            <div
                v-if="localConfig.algorithmSettings.algorithm !== 'ordered' && isCustomDateRange"
                class="config-field"
            >
                <label>{{ dateRangeValueLabel }}</label>
                <a-input-number
                    v-model="localConfig.algorithmSettings.dateRangeValue"
                    :min="1"
                    :step="1"
                    style="width: 100%;"
                    @change="emitChange"
                />
            </div>

            <div class="config-field">
                <a-checkbox 
                    v-model="localConfig.algorithmSettings.checkOnlineStatus"
                    @change="emitChange"
                >
                    {{ $t("contactModuleRules.changeOwnerConfig.onlyOnline") }}
                </a-checkbox>
            </div>
        </div>

        <!-- Random Method -->
        <div v-if="localConfig.method === 'random'" class="random-config">
            <!-- User Selection -->
            <div class="config-field">
                <label>{{ $t("contactModuleRules.changeOwnerConfig.userList") }}</label>
                <a-checkbox v-model="localConfig.useVisibilityControl" @change="onVisibilityControlChange">
                    {{ $t("contactModuleRules.changeOwnerConfig.channelAccessUsers") }}
                </a-checkbox>
            </div>

            <div v-if="!localConfig.useVisibilityControl" class="config-field">
                <a-select
                    v-model="localConfig.userList"
                    mode="multiple"
                    :placeholder="$t('contactModuleRules.changeOwnerConfig.selectUsers')"
                    style="width: 100%;"
                    :filter-option="filterOption"
                    @change="emitChange"
                >
                    <a-select-option
                        v-for="user in users"
                        :key="user._id"
                        :value="user._id"
                    >
                        {{ user.name }}
                    </a-select-option>
                </a-select>
            </div>
        </div>

        <!-- Zoho Sync (for all methods) -->
        <div class="config-field zoho-sync">
            <a-checkbox 
                v-model="localConfig.syncToZoho"
                @change="emitChange"
            >
                {{ $t("contactModuleRules.changeOwnerSyncToZoho") }}
            </a-checkbox>
            <small>{{ $t("contactModuleRules.changeOwnerZohoHint") }}</small>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import log2 from "../../../../../../utils/log2";

const log = log2("ChangeOwnerConfig");

export default {
    name: "ChangeOwnerConfig",
    props: {
        value: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            localConfig: {},
            isOwnerless: false
        };
    },
    computed: {
        ...mapState({
            users: state => state.user.users.data
        }),
        isCustomDateRange() {
            const dr = this.localConfig.algorithmSettings && this.localConfig.algorithmSettings.dateRange;
            return dr === 'last_n_min' || dr === 'last_n_hours' || dr === 'last_n_days';
        },
        dateRangeValueLabel() {
            const dr = this.localConfig.algorithmSettings && this.localConfig.algorithmSettings.dateRange;
            if (dr === 'last_n_min') {
                return this.$t("contactModuleRules.changeOwnerConfig.minuteCount");
            }
            if (dr === 'last_n_hours') {
                return this.$t("contactModuleRules.changeOwnerConfig.hourCount");
            }
            return this.$t("contactModuleRules.changeOwnerConfig.dayCount");
        }
    },
    watch: {
        value: {
            handler(newVal) {
                if (newVal && JSON.stringify(newVal) !== JSON.stringify(this.localConfig)) {
                    this.localConfig = JSON.parse(JSON.stringify(newVal));
                    this.isOwnerless = this.localConfig.method === 'static' && this.localConfig.staticUserId === null;
                }
            },
            immediate: true,
            deep: true
        },
        'localConfig.method'(newVal) {
            if (newVal === 'static') {
                this.isOwnerless = this.localConfig.staticUserId === null;
            }
            this.emitChange();
        }
    },
    methods: {
        emitChange() {
            this.$emit("input", JSON.parse(JSON.stringify(this.localConfig)));
        },

        onMethodChange() {
            // Reset relevant fields when method changes
            if (this.localConfig.method === 'static') {
                this.localConfig.userList = [];
                this.localConfig.useVisibilityControl = false;
            }
            this.emitChange();
        },

        onOwnerlessChange(checked) {
            if (checked) {
                this.localConfig.staticUserId = null;
            } else {
                this.localConfig.staticUserId = "";
            }
            this.emitChange();
        },

        onVisibilityControlChange(checked) {
            if (checked) {
                this.localConfig.userList = [];
            }
            this.emitChange();
        },

        onDateRangeChange() {
            // last_n_* dışındaki seçeneklerde custom değer anlamsız, temizle
            if (!this.isCustomDateRange) {
                this.localConfig.algorithmSettings.dateRangeValue = null;
            } else if (!this.localConfig.algorithmSettings.dateRangeValue) {
                this.localConfig.algorithmSettings.dateRangeValue = 1;
            }
            this.emitChange();
        },

        filterOption(input, option) {
            return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        }
    }
};
</script>

<style lang="scss" scoped>
.change-owner-config {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .config-field {
        label {
            display: block;
            font-weight: 600;
            margin-bottom: 8px;
            color: #1a1a1a;
        }

        small {
            display: block;
            color: #999;
            font-size: 12px;
            margin-top: 6px;
        }

        &.zoho-sync {
            padding: 12px;
            background: #fffbe6;
            border: 1px solid #ffe58f;
            border-radius: 6px;
        }
    }
}
</style>

